import { createOAuth2Client } from "@extrahorizon/javascript-sdk";

import { getRedirectedFunctionName, isSchemaRedirected, schemaRedirects } from "./schemaRedirects";
import { createUrlInterceptor } from "./urlInterceptor";

// Re-export utilities for use in components
export { getRedirectedFunctionName, isSchemaRedirected, schemaRedirects };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let exhClient: any = null;

const REFRESH_TOKEN_KEY = "exh_refresh_token";
const ACCESS_TOKEN_KEY = "exh_access_token";
const TOKEN_EXPIRY_KEY = "exh_token_expiry";

/**
 * Token data structure returned by SDK
 */
interface TokenData {
  refreshToken?: string;
  accessToken?: string;
  expiresIn?: number;
}

/**
 * Save token data to localStorage
 */
const saveTokenData = (tokenData: TokenData) => {
  try {
    localStorage.setItem(REFRESH_TOKEN_KEY, tokenData.refreshToken || "");
    if (tokenData.accessToken) {
      localStorage.setItem(ACCESS_TOKEN_KEY, tokenData.accessToken);
    }
    if (tokenData.expiresIn) {
      const expiryTime = Date.now() + tokenData.expiresIn * 1000;
      localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to save tokens:", error);
  }
};

/**
 * Get refresh token from localStorage
 */
export const getStoredRefreshToken = (): string | null => {
  try {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch {
    return null;
  }
};

/**
 * Clear refresh token from localStorage
 */
export const clearStoredRefreshToken = () => {
  try {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to clear refresh token:", error);
  }
};

/**
 * Clear all ExtraHorizon tokens from localStorage
 * Useful for debugging or forcing a fresh login
 */
export const clearAllStoredTokens = () => {
  try {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to clear tokens:", error);
  }
};

/**
 * Get stored access token from localStorage
 */
export const getStoredAccessToken = (): string | null => {
  try {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  } catch {
    return null;
  }
};

/**
 * Check if stored tokens are still valid (not expired)
 */
export const areStoredTokensValid = (): boolean => {
  try {
    const expiryStr = localStorage.getItem(TOKEN_EXPIRY_KEY);
    if (!expiryStr) {
      return false;
    }
    const expiryTime = parseInt(expiryStr, 10);
    return Date.now() < expiryTime;
  } catch {
    return false;
  }
};

/**
 * Initialize the ExtraHorizon SDK client with OAuth2 configuration
 * Should be called once during app initialization
 * @param clientId - OAuth2 client ID from environment config
 * @returns The initialized SDK client
 */
export const initializeExhClient = (clientId: string) => {
  if (exhClient) {
    return exhClient;
  }

  try {
    // Use direct ExtraHorizon API host instead of proxy for Vercel compatibility
    const exhHost = process.env.REACT_APP_EXTRAHORIZON_HOST;
    if (!exhHost) {
      throw new Error("REACT_APP_EXTRAHORIZON_HOST environment variable is required but not set");
    }

    exhClient = createOAuth2Client({
      host: exhHost,
      clientId,
      // Persist tokens to localStorage whenever SDK refreshes access token
      freshTokensCallback: (tokenData) => {
        saveTokenData(tokenData);
      },
    });

    // Add URL interceptor for schema-specific API function redirection
    const urlInterceptor = createUrlInterceptor(
      schemaRedirects,
      process.env.NODE_ENV === "development" // Enable logging in development
    );
    exhClient.raw.interceptors.request.use(urlInterceptor);

    // Log active redirections in development
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.log("📋 Active Schema Redirections:", schemaRedirects);
      Object.entries(schemaRedirects).forEach(([schema, methodMap]) => {
        Object.entries(methodMap).forEach(([method, functionName]) => {
          // eslint-disable-next-line no-console
          console.log(`  ✅ ${method} ${schema} -> /tasks/v1/api/${functionName}/documents`);
        });
      });
    }

    return exhClient;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to initialize ExtraHorizon SDK client:", error);
    throw error;
  }
};

/**
 * Get the initialized ExtraHorizon SDK client
 * @returns The SDK client if initialized
 * @throws Error if client is not initialized
 */
export const getExhClient = () => {
  if (!exhClient) {
    throw new Error("ExtraHorizon client not initialized. Call initializeExhClient() first.");
  }
  return exhClient;
};

/**
 * Check if the ExtraHorizon client is initialized
 * @returns true if client is initialized, false otherwise
 */
export const isExhClientInitialized = () => {
  return exhClient !== null;
};

/**
 * Reset the SDK client (useful for testing or logout)
 */
export const resetExhClient = () => {
  exhClient = null;
};
